/**
 * 上传页入口 query 契约：
 * - project_id / projectId → 锁定已有文件夹，停在「准备条目」
 * - work_item_id → 恢复已有条目，进入文件上传
 *
 * 详情页「添加视频/文档条目」传的是文件夹 id，不能当成 WorkItem.id。
 */
import type { LocationQuery, LocationQueryValue } from 'vue-router'

import type { WorkItem } from '@/modules/project/api/work-item'

export type UploadEntryIntent =
  | { kind: 'blank' }
  | { kind: 'lock-folder'; projectId: number }
  | { kind: 'resume-work-item'; workItemId: number }

export type UploadEntryContext =
  | { kind: 'blank' }
  | { kind: 'lock-folder'; projectId: number; title: string }
  | { kind: 'resume-work-item'; workItem: WorkItem }
  | { kind: 'error'; reason: 'folder' | 'work-item'; error: unknown }

export interface UploadEntryApis {
  getProject: (id: number) => Promise<{ id: number; title: string }>
  getWorkItem: (id: number) => Promise<WorkItem>
}

function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value
  if (raw == null || raw === '') return undefined
  return String(raw)
}

function parsePositiveInt(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): number | undefined {
  const raw = firstQueryValue(value)
  if (raw == null) return undefined
  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined
  return parsed
}

export function parseUploadEntryQuery(query: LocationQuery): UploadEntryIntent {
  const workItemId = parsePositiveInt(query.work_item_id)
  if (workItemId != null) {
    return { kind: 'resume-work-item', workItemId }
  }

  const projectId =
    parsePositiveInt(query.project_id) ?? parsePositiveInt(query.projectId)
  if (projectId != null) {
    return { kind: 'lock-folder', projectId }
  }

  return { kind: 'blank' }
}

export interface UploadPageEntryState {
  projectId: number | undefined
  currentStep: 0 | 1
  lockedFolderProjectId: number | null
  title: string
  original_language: string
  target_language: string | undefined
  errorLog?: { message: string; error: unknown }
  clearVideoProjectCache: boolean
}

export function toUploadPageEntryState(entry: UploadEntryContext): UploadPageEntryState {
  if (entry.kind === 'lock-folder') {
    return {
      projectId: undefined,
      currentStep: 0,
      lockedFolderProjectId: entry.projectId,
      title: entry.title,
      original_language: '',
      target_language: undefined,
      clearVideoProjectCache: true,
    }
  }

  if (entry.kind === 'resume-work-item') {
    const workItem = entry.workItem
    return {
      projectId: workItem.id,
      currentStep: 1,
      lockedFolderProjectId: null,
      title: workItem.file_name || '',
      original_language: workItem.source_language || '',
      target_language: workItem.target_language || undefined,
      clearVideoProjectCache: false,
    }
  }

  return {
    projectId: undefined,
    currentStep: 0,
    lockedFolderProjectId: null,
    title: '',
    original_language: '',
    target_language: undefined,
    errorLog:
      entry.kind === 'error'
        ? {
            message: entry.reason === 'folder' ? '加载项目失败:' : '加载条目失败:',
            error: entry.error,
          }
        : undefined,
    clearVideoProjectCache: true,
  }
}

export async function loadUploadEntryContext(
  query: LocationQuery,
  apis: UploadEntryApis,
): Promise<UploadEntryContext> {
  const intent = parseUploadEntryQuery(query)

  if (intent.kind === 'blank') {
    return { kind: 'blank' }
  }

  if (intent.kind === 'lock-folder') {
    try {
      const project = await apis.getProject(intent.projectId)
      return {
        kind: 'lock-folder',
        projectId: project.id,
        title: project.title,
      }
    } catch (error) {
      return { kind: 'error', reason: 'folder', error }
    }
  }

  try {
    const workItem = await apis.getWorkItem(intent.workItemId)
    return { kind: 'resume-work-item', workItem }
  } catch (error) {
    return { kind: 'error', reason: 'work-item', error }
  }
}
